import useResizeObserver from '@react-hook/resize-observer';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import _ from 'underscore';

import NoContentImage from '../../../../../static/empty_state_asset_menu.svg?react';
import { trimChars } from '../../../../utils/text/text';
import { Design } from '../../../../viewer/elements/design';
//
import { AssetWithFileInfo } from '../../../../viewer/elements/file-info/types';
import { Geometry } from '../../../../viewer/elements/geometry';
import { Model } from '../../../../viewer/elements/model';
import { Poi } from '../../../../viewer/elements/poi/poi';
import { TerrainModel } from '../../../../viewer/elements/terrain-model';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { reset } from '../../../utils/styled-reset';
import { IconPanel } from '../../icon-panel/icon-panel';
import { useAssetFilterStore } from '../state/asset-filter-store';
import { AssetState } from '../state/asset-state';
import { useAssetsStore } from '../state/assets-store';
import { CadGroupVisibilityState } from '../state/cad-group-visibility-state';
import { CategoryContext } from '../state/category-state';
import { CategoryVisibilityState } from '../state/category-visibility-state';
import { CheckBoxState } from '../state/checkbox-state';
import { AreaAsset } from './assets/area/area-asset';
import { DesignAsset } from './assets/design/design-asset';
import { LineAsset } from './assets/line/line-asset';
import { ModelAsset } from './assets/model/model-asset';
import { PoiAsset } from './assets/poi/poi-asset';
import { TerrainModelAsset } from './assets/terrain-model/terrain-model-asset';
import { CadGroup, CadGroupItem } from './components/asset-blocks/cad-group';
import { Category } from './components/asset-blocks/category';
import { VirtualAssetList } from './components/virtual-asset-list';

type Props = {
  assetNameFilter: string;
};

// Note: all AssetManager-driven fetching/refreshing was moved to AssetsProvider.

function useAssetManagerAssets() {
  // Temporary shim now that AssetsProvider owns refresh
  const categoryContext = React.useContext(CategoryContext);
  const { assets, isLoaded } = useAssetsStore();
  const errorList = null;

  // Trigger recomputation on category changes (legacy dependency)
  React.useEffect(() => {
    void categoryContext.refreshNonce;
  }, [categoryContext.refreshNonce]);

  return { assets, isLoaded, errorList };
}

function filterAndSort<T extends { name: string }>(list: T[], filter: string): T[] {
  return list
    .filter((x) => x.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function groupCadFiles(cadFiles: AssetWithFileInfo[]) {
  return _.chain(cadFiles)
    .groupBy((asset) => {
      const folderId = asset.fileInfo?.folderId;
      const path = asset.fileInfo?.path.substring(0, asset.fileInfo.path.lastIndexOf('/'));

      // Group by folder id and path.
      // Returning object with these as properties isn't working.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `${folderId}#${path}`;
    })
    .map((value, key: string) => {
      const folderId = key.substr(0, key.indexOf('#'));
      const path = key.substr(key.indexOf('#') + 1);

      return {
        folderId,
        path: trimChars(path, ['/', ' ']),
        // @ts-ignore
        assets: _.sortBy(value, (asset) => asset.name.toLowerCase()),
      };
    })
    .sortBy((asset) => asset.path.toLowerCase())
    .value();
}

const AssetMenuList = (props: Props) => {
  const { t } = useTranslation();
  const { assets: mirroredAssets, filterAssetsByType } = useAssetsStore();
  const { assets, errorList, isLoaded } = useAssetManagerAssets();
  const { isAssetVisible, loading: filterLoading } = useAssetFilterStore();
  const theme = useTheme();

  const visibleAssets = useMemo(() => {
    const list = assets ?? mirroredAssets;
    return list.filter(isAssetVisible);
  }, [assets, mirroredAssets, isAssetVisible]);

  // Refs must be stable across renders to avoid useResizeObserver re-running and causing setState loops.
  const categoryRefs = React.useRef({
    design: React.createRef<HTMLDivElement>(),
    area: React.createRef<HTMLDivElement>(),
    line: React.createRef<HTMLDivElement>(),
    poi: React.createRef<HTMLDivElement>(),
    cad: React.createRef<HTMLDivElement>(),
    model: React.createRef<HTMLDivElement>(),
    terrainModel: React.createRef<HTMLDivElement>(),
  }).current;
  const [designCategoryHeight, setDesignCategoryHeight] = React.useState(0);
  const [areaCategoryHeight, setAreaCategoryHeight] = React.useState(0);
  const [lineCategoryHeight, setLineCategoryHeight] = React.useState(0);
  const [poiCategoryHeight, setPoiCategoryHeight] = React.useState(0);
  const [cadCategoryHeight, setCadCategoryHeight] = React.useState(0);
  const [modelCategoryHeight, setModelCategoryHeight] = React.useState(0);
  const [terrainModelCategoryHeight, setTerrainModelCategoryHeight] = React.useState(0);

  const designs = useMemo<Design[]>(() => {
    return filterAndSort(filterAssetsByType('design', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);

  const areas = useMemo<Geometry[]>(() => {
    return filterAndSort(filterAssetsByType('area', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);

  const lines = useMemo<Geometry[]>(() => {
    return filterAndSort(filterAssetsByType('line', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);

  const pois = useMemo<Poi[]>(() => {
    return filterAndSort(filterAssetsByType('poi', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);

  const cadFiles = useMemo<CadGroupItem[]>(() => {
    return groupCadFiles(
      filterAndSort(
        [
          ...(filterAssetsByType('dxf', visibleAssets) as AssetWithFileInfo[]),
          ...(filterAssetsByType('glb', visibleAssets) as AssetWithFileInfo[]),
        ],
        props.assetNameFilter,
      ),
    );
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);
  const models = useMemo<Model[]>(() => {
    return filterAndSort(filterAssetsByType('model', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);
  const terrainModels = useMemo<TerrainModel[]>(() => {
    return filterAndSort(filterAssetsByType('terrain-model', visibleAssets), props.assetNameFilter);
  }, [visibleAssets, props.assetNameFilter, filterAssetsByType]);

  const heightRefs = React.useRef({
    design: 0,
    area: 0,
    line: 0,
    poi: 0,
    cad: 0,
    model: 0,
    terrainModel: 0,
  }).current;
  useResizeObserver(categoryRefs.design, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.design) {
      heightRefs.design = size;
      setDesignCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.area, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.area) {
      heightRefs.area = size;
      setAreaCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.line, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.line) {
      heightRefs.line = size;
      setLineCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.poi, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.poi) {
      heightRefs.poi = size;
      setPoiCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.cad, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.cad) {
      heightRefs.cad = size;
      setCadCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.model, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.model) {
      heightRefs.model = size;
      setModelCategoryHeight(size);
    }
  });
  useResizeObserver(categoryRefs.terrainModel, (entry) => {
    const size = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
    if (size !== heightRefs.terrainModel) {
      heightRefs.terrainModel = size;
      setTerrainModelCategoryHeight(size);
    }
  });

  const noAssets =
    designs.length === 0 &&
    areas.length === 0 &&
    lines.length === 0 &&
    pois.length === 0 &&
    cadFiles.length === 0 &&
    models.length === 0 &&
    terrainModels.length === 0;

  if (errorList) {
    return errorList;
  }

  if (!isLoaded || filterLoading) {
    return (
      <Component>
        <NoContent>
          <IconPanel
            header={{
              icon: { icon: 'spinner', spin: true, size: '3x', color: theme.color.gray.medium },
            }}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('emptyState.loading.title', { ns: 'skyviewAssetMenu' }),
            }}
          </IconPanel>
        </NoContent>
      </Component>
    );
  }

  if (noAssets) {
    return (
      <Component>
        <NoContent>
          {props.assetNameFilter.length === 0 ? (
            <IconPanel header={<NoContentImage />} responsiveness={{ compressed: false }}>
              {{
                title: t('emptyState.noSearchText.title', { ns: 'skyviewAssetMenu' }),
                info: (
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="emptyState.noSearchText.info"
                    ns="skyviewAssetMenu"
                  />
                ),
              }}
            </IconPanel>
          ) : (
            <IconPanel header={<NoContentImage />} responsiveness={{ compressed: false }}>
              {{
                title: t('emptyState.searchText.title', { ns: 'skyviewAssetMenu' }),
                info: t('emptyState.searchText.info', { ns: 'skyviewAssetMenu' }),
              }}
            </IconPanel>
          )}
        </NoContent>
      </Component>
    );
  }

  return (
    <Component>
      {designs.length > 0 && (
        <CheckBoxState assets={designs} categoryType="design">
          <CategoryVisibilityState
            assets={designs}
            categoryType="design"
            height={designCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.design}
              icon={{ icon: ['fal', 'pen-ruler'], fixedWidth: true }}
              title={t('design.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {designs.map((x) => (
                <AssetState key={`design-${x.uuid}`}>
                  <DesignAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {areas.length > 0 && (
        <CheckBoxState assets={areas} categoryType="area">
          <CategoryVisibilityState assets={areas} categoryType="area" height={areaCategoryHeight}>
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.area}
              icon={{ icon: ['fal', 'draw-polygon'], fixedWidth: true }}
              title={t('area.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {areas.map((x) => (
                <AssetState key={`area-${x.uuid}`}>
                  <AreaAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {lines.length > 0 && (
        <CheckBoxState assets={lines} categoryType="line">
          <CategoryVisibilityState assets={lines} categoryType="line" height={lineCategoryHeight}>
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.line}
              icon={{ icon: ['fal', 'wave-triangle'], fixedWidth: true }}
              title={t('line.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {lines.map((x) => (
                <AssetState key={`line-${x.uuid}`}>
                  <LineAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {pois.length > 0 && (
        <CheckBoxState assets={pois} categoryType="poi">
          <CategoryVisibilityState assets={pois} categoryType="poi" height={poiCategoryHeight}>
            <Category
              collapseTactic="shrink"
              headerRef={categoryRefs.poi}
              icon={{ icon: ['fal', 'location-dot'], fixedWidth: true }}
              title={'POI'}
            >
              <VirtualAssetList
                assetType="poi"
                getAssetId={(p: Poi) => p.assetId!}
                items={pois}
                maxHeight={'50em'}
                onScrolledIntoIndex={(poi: Poi) =>
                  // We need to push 'OpenPoiPreviewDialog' to ensure that elements that are scrolled to
                  // open the image dialog.
                  //
                  // This because while the element is outside of the VirtualAssetList viewport,
                  // it does not listen to topics. So we need to push our desired topic to the React element.
                  queueMicrotask(() => {
                    setTimeout(() => publish(SubscriptionTopic.OpenPoiPreviewDialog, poi), 100);
                  })
                }
              >
                {(item: Poi) => (
                  <AssetState key={`pois-${item.uuid}`}>
                    <PoiAsset asset={item} />
                  </AssetState>
                )}
              </VirtualAssetList>
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {cadFiles.length > 0 && (
        <CheckBoxState assets={cadFiles.flatMap((x) => x.assets)} categoryType="cad">
          <CategoryVisibilityState
            assets={cadFiles.flatMap((x) => x.assets)}
            categoryType="cad"
            height={cadCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.cad}
              icon={{ icon: ['fal', 'cubes'], fixedWidth: true }}
              title={t('cad.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {cadFiles.map((x) => {
                return (
                  <CadGroupVisibilityState assets={x.assets} folderId={x.folderId} key={x.folderId}>
                    <CadGroup cadGroup={x} name={x.path} stickyTopPos={cadCategoryHeight} />
                  </CadGroupVisibilityState>
                );
              })}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {models.length > 0 && (
        <CheckBoxState assets={models} categoryType="model">
          <CategoryVisibilityState
            assets={models}
            categoryType="model"
            height={modelCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.model}
              icon={{ icon: ['fal', 'cube'], fixedWidth: true }}
              title={t('model.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {models.map((x) => (
                <AssetState key={`model-${x.uuid}`}>
                  <ModelAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}

      {terrainModels.length > 0 && (
        <CheckBoxState assets={terrainModels} categoryType="terrain-model">
          <CategoryVisibilityState
            assets={terrainModels}
            categoryType="terrain-model"
            height={terrainModelCategoryHeight}
          >
            <Category
              collapseTactic="hide"
              headerRef={categoryRefs.terrainModel}
              icon={{ icon: ['fal', 'mountains'], fixedWidth: true }}
              title={t('terrainModel.categoryName', { ns: 'skyviewAssetMenu' })}
            >
              {terrainModels.map((x) => (
                <AssetState key={`terrain-model-${x.uuid}`}>
                  <TerrainModelAsset asset={x} />
                </AssetState>
              ))}
            </Category>
          </CategoryVisibilityState>
        </CheckBoxState>
      )}
    </Component>
  );
};

const Component = styled.div`
  ${reset}

  display: flex;
  flex-direction: column;
  gap: 1em;

  min-height: 0;
  overflow: auto;
`;

const NoContent = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
`;

AssetMenuList.styled = Component;

export { AssetMenuList, Props as AssetMenuListProps };
